import { useMemo } from 'react';
import { NCFileLevelSubScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { TRACEABILITY_GRAPH_NODE_CONFIG } from '@v2/pages/common/components/TraceabilityGraph/constants';
import { buildBatchSummaries, buildJobSummaries, buildMachines, buildPart, buildProductionOrders } from '@v2/pages/common/components/TraceabilityGraph/helpers/buildEntitySummaries';
import { limitNodes, normalizeNodeId } from '@v2/pages/common/components/TraceabilityGraph/helpers/traceabilityGraphUtils';
import {
  NcFileMachineRun,
  TraceabilityColumn,
  TraceabilityEntitySummary,
  TraceabilityGraphProps,
  TraceabilityNode,
  TraceabilityNodeType,
} from '@v2/pages/common/components/TraceabilityGraph/types';

export const useManageTraceabilityGraph = ({
  ncFile,
  ncFileLoaded,
  jobs,
  batches,
  batchesLoading,
  batchesTotalCount,
  documentCount,
  documentsLoading,
  onOpenPart,
  onOpenProductionOrder,
  onOpenJob,
  onOpenBatch,
  onOpenNcFileSubScreen,
}: TraceabilityGraphProps) => {
  // Depend on primitive fields rather than the whole `ncFile` object so a fresh
  // Redux reference (background refresh/polling) doesn't rebuild the entire graph.
  const ncFileId = ncFile?.ncFileId ?? null;
  const ncFileName = ncFile?.ncFileName ?? null;
  const ncFileStatus = ncFile?.ncFileStatus ?? null;
  const versionsCount = ncFile?.versions?.length ?? 0;
  const partId = ncFile?.partId ?? null;
  const partNumber = ncFile?.partNumber ?? null;
  const partDescription = ncFile?.partDescription ?? null;
  const partImageHash = ncFile?.partImageHash ?? null;
  const partImageDocumentId = ncFile?.partImageDocumentId ?? null;

  const ncFileRun = ncFile as NcFileMachineRun | null;
  const firstRanByMachineId = ncFileRun?.firstRanByMachineId ?? null;
  const firstRanByMachineName = ncFileRun?.firstRanByMachineName ?? null;
  const lastRanByMachineId = ncFileRun?.lastRanByMachineId ?? null;
  const lastRanByMachineName = ncFileRun?.lastRanByMachineName ?? null;

  const links = useMemo(() => ncFile?.links ?? [], [ncFile?.links]);

  const part = useMemo<TraceabilityEntitySummary | null>(
    () => buildPart({ links, partId, partNumber, partDescription, partImageHash, partImageDocumentId }),
    [links, partDescription, partId, partImageDocumentId, partImageHash, partNumber]
  );

  const productionOrders = useMemo(() => buildProductionOrders({ links, jobs }), [jobs, links]);

  const jobSummaries = useMemo(() => buildJobSummaries({ jobs, links }), [jobs, links]);

  const batchSummaries = useMemo(() => buildBatchSummaries(batches), [batches]);

  const machines = useMemo(
    () => buildMachines({ batches, jobs, firstRanByMachineId, firstRanByMachineName, lastRanByMachineId, lastRanByMachineName }),
    [batches, jobs, firstRanByMachineId, firstRanByMachineName, lastRanByMachineId, lastRanByMachineName]
  );

  const ncFileNode = useMemo<TraceabilityNode | null>(() => {
    if (!ncFileId) return null;

    return {
      id: ncFileId,
      nodeId: `nc-file-${ncFileId}`,
      type: TraceabilityNodeType.NC_FILE,
      eyebrow: 'NC File',
      title: ncFileName ?? `#${ncFileId}`,
      subtitle: ncFileStatus ?? 'Status unknown',
      icon: TRACEABILITY_GRAPH_NODE_CONFIG[TraceabilityNodeType.NC_FILE].icon,
      onPress: () => onOpenNcFileSubScreen?.(NCFileLevelSubScreen.GENERAL),
    };
  }, [ncFileId, ncFileName, ncFileStatus, onOpenNcFileSubScreen]);

  const columns = useMemo<TraceabilityColumn[]>(() => {
    const partNodes: TraceabilityNode[] = part
      ? [
          {
            ...part,
            nodeId: `part-${part.id}`,
            type: TraceabilityNodeType.PART,
            eyebrow: 'Part',
            icon: TRACEABILITY_GRAPH_NODE_CONFIG[TraceabilityNodeType.PART].icon,
            onPress: () => onOpenPart?.(part),
          },
        ]
      : [];

    const orderNodes = limitNodes(
      productionOrders.map((order) => ({
        ...order,
        nodeId: `order-${order.id}`,
        type: TraceabilityNodeType.PRODUCTION_ORDER,
        eyebrow: 'Production Order',
        icon: TRACEABILITY_GRAPH_NODE_CONFIG[TraceabilityNodeType.PRODUCTION_ORDER].icon,
        onPress: () => onOpenProductionOrder?.(order),
      })),
      TraceabilityNodeType.PRODUCTION_ORDER
    );

    const jobNodes = limitNodes(
      jobSummaries.map((job) => ({
        ...job,
        nodeId: `job-${job.id}`,
        type: TraceabilityNodeType.JOB,
        eyebrow: 'Job',
        icon: TRACEABILITY_GRAPH_NODE_CONFIG[TraceabilityNodeType.JOB].icon,
        onPress: () => onOpenJob?.(job),
      })),
      TraceabilityNodeType.JOB
    );

    const batchNodes = limitNodes(
      batchSummaries.map((batch) => ({
        ...batch,
        nodeId: `batch-${batch.id}`,
        type: TraceabilityNodeType.BATCH,
        eyebrow: 'Batch',
        icon: TRACEABILITY_GRAPH_NODE_CONFIG[TraceabilityNodeType.BATCH].icon,
        onPress: () => onOpenBatch?.(batch),
      })),
      TraceabilityNodeType.BATCH
    );

    const machineNodes = limitNodes(
      machines.map((machine) => ({
        ...machine,
        nodeId: `machine-${machine.id}`,
        type: TraceabilityNodeType.MACHINE,
        eyebrow: 'Machine',
        icon: TRACEABILITY_GRAPH_NODE_CONFIG[TraceabilityNodeType.MACHINE].icon,
      })),
      TraceabilityNodeType.MACHINE
    );

    const evidenceNodes: TraceabilityNode[] = [
      {
        id: ncFileId ?? 0,
        nodeId: `versions-${ncFileId ?? 'missing'}`,
        type: TraceabilityNodeType.VERSIONS,
        eyebrow: 'Versions',
        title: `${versionsCount} version${versionsCount === 1 ? '' : 's'}`,
        subtitle: 'Compare NC file changes',
        icon: TRACEABILITY_GRAPH_NODE_CONFIG[TraceabilityNodeType.VERSIONS].icon,
        disabled: !ncFileId,
        onPress: () => onOpenNcFileSubScreen?.(NCFileLevelSubScreen.VERSIONS),
      },
      {
        id: ncFileId ?? 0,
        nodeId: `documents-${ncFileId ?? 'missing'}`,
        type: TraceabilityNodeType.DOCUMENTS,
        eyebrow: 'Documents',
        title: documentsLoading ? 'Loading' : `${documentCount ?? 0} document${documentCount === 1 ? '' : 's'}`,
        subtitle: 'Images, drawings, PDFs',
        icon: TRACEABILITY_GRAPH_NODE_CONFIG[TraceabilityNodeType.DOCUMENTS].icon,
        disabled: !ncFileId,
        onPress: () => onOpenNcFileSubScreen?.(NCFileLevelSubScreen.DOCUMENTS),
      },
      {
        id: ncFileId ?? 0,
        nodeId: `events-${ncFileId ?? 'missing'}`,
        type: TraceabilityNodeType.EVENTS,
        eyebrow: 'Events',
        title: batchesTotalCount == null ? 'Machine events' : `${batchesTotalCount} batch${batchesTotalCount === 1 ? '' : 'es'}`,
        subtitle: 'Setup, active, change, cycle',
        icon: TRACEABILITY_GRAPH_NODE_CONFIG[TraceabilityNodeType.EVENTS].icon,
        disabled: !ncFileId,
        onPress: () => onOpenNcFileSubScreen?.(NCFileLevelSubScreen.TIMES),
      },
    ];

    // Fixed, deterministic column set — never filtered. Missing entities leave an empty column.
    return [
      { id: 'part', title: 'Part', nodes: partNodes },
      { id: 'production-orders', title: 'Production Orders', nodes: orderNodes },
      { id: 'jobs', title: 'Jobs', nodes: jobNodes },
      { id: 'batches', title: 'Batches', nodes: batchNodes, loading: batchesLoading },
      { id: 'machines', title: 'Machines', nodes: machineNodes },
      { id: 'nc-file', title: 'NC Program', nodes: ncFileNode ? [ncFileNode] : [] },
      { id: 'evidence', title: 'Evidence', nodes: evidenceNodes },
    ];
  }, [
    batchSummaries,
    batchesLoading,
    batchesTotalCount,
    documentCount,
    documentsLoading,
    jobSummaries,
    machines,
    ncFileId,
    versionsCount,
    ncFileNode,
    onOpenBatch,
    onOpenJob,
    onOpenNcFileSubScreen,
    onOpenPart,
    onOpenProductionOrder,
    part,
    productionOrders,
  ]);

  // Real traceability content exists independently of the always-present Evidence column.
  const hasContent =
    Boolean(ncFileNode) ||
    Boolean(part) ||
    productionOrders.length > 0 ||
    jobSummaries.length > 0 ||
    batchSummaries.length > 0 ||
    machines.length > 0;

  const isLoading = !ncFileLoaded && !ncFile;

  return {
    columns,
    hasContent,
    isLoading,
    normalizeNodeId,
  };
};
